import React from 'react';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import checkIsTicketGroupOnSale from '@dashboard/services/event/checkIsTicketGroupOnSale';
import checkIsTicketGroupScheduled from '@repo/checkout/lib/checkIsTicketGroupScheduled';
import checkIsTicketGroupSoldOut from '@repo/checkout/lib/checkIsTicketGroupSoldOut';
import checkIsTicketGroupNotOnSale from '@repo/checkout/lib/checkIsTicketGroupNotOnSale';

export default function TicketGroupBadges({
  page,
  ticketGroup,
  allTicketGroups = [],
  showOnlyOneBadge = false,
}) {
  let primaryBadge = null;
  let hiddenBadge = null;
  let disabledBadge = null;

  /**
   * Archived takes priority over all badges and we only show the archived badge.
   */
  if (ticketGroup.status === 'archived') {
    return (
      <div className="flex gap-x-1 flex-wrap">
        <div>
          <Tooltip label="This ticket is shown as archived to customers.">
            <StatusBadge status="archived" label="Archived" />
          </Tooltip>
        </div>
      </div>
    );
  }

  const isOnSale = checkIsTicketGroupOnSale({ page, ticketGroup, allTicketGroups });
  const isSoldOut = checkIsTicketGroupSoldOut(ticketGroup, { excludeTicketTypes: true });
  const isUnavailable = ticketGroup.availabilityStatus === 'unavailable';
  const { isScheduled, hasInvalidReference } = checkIsTicketGroupScheduled({
    ticketGroup,
    allTicketGroups,
  });

  const isNotOnSale = checkIsTicketGroupNotOnSale({
    ticketGroup,
    isCanceled: page.eventDetails?.status === 'canceled',
    hasInvalidReference,
    isOnSale,
    isScheduled,
  });

  if (isSoldOut) {
    primaryBadge = {
      content: <StatusBadge status="sold_out" label="Sold out" />,
      tooltip: 'This ticket group no longer has availability.',
    };
  } else if (isUnavailable) {
    primaryBadge = {
      content: <StatusBadge status="unavailable" label="Unavailable" />,
      tooltip: 'This ticket group is shown as unavailable to customers.',
    };
  } else if (ticketGroup.availabilityStatus === 'available' && !isOnSale && isScheduled) {
    primaryBadge = {
      content: <StatusBadge status="scheduled" label="Scheduled" />,
      tooltip: 'This ticket group is scheduled to go on sale.',
    };
  } else if (
    (ticketGroup.availabilityStatus === 'available' || ticketGroup.availabilityStatus == null) &&
    isOnSale
  ) {
    primaryBadge = {
      content: <StatusBadge status="on_sale" label="On sale" />,
      tooltip: 'This ticket group is currently on sale.',
    };
  }

  if (ticketGroup.hidden) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket group is hidden.',
    };
  }

  if (hiddenBadge == null && ticketGroup.hideWhenNotOnSale && isNotOnSale) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket group is hidden when not on sale.',
    };
  }

  if (hiddenBadge == null && ticketGroup.hideWhenScheduled && isScheduled) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket group is hidden when scheduled to go sale.',
    };
  }

  if (hiddenBadge == null && ticketGroup.hideWhenUnavailable && isUnavailable) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket group is hidden when unavailable.',
    };
  }

  if (hiddenBadge == null && ticketGroup.hideWhenSoldOut && isSoldOut) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket group is hidden when sold out.',
    };
  }

  if (ticketGroup.status === 'disabled') {
    disabledBadge = {
      content: <StatusBadge status="disabled" label="Disabled" />,
      tooltip: 'Disabled ticket groups will not be shown to customers.',
    };
  }

  // If no primary badge is set, show not on sale badge
  if (!primaryBadge && ticketGroup.status === 'enabled') {
    primaryBadge = {
      content: <StatusBadge status="sale_ended" label="Not on sale" />,
      tooltip: 'This ticket group is currently not on sale.',
    };
  }

  const badges = [];
  if (showOnlyOneBadge) {
    if (disabledBadge) {
      badges.push({ key: 'disabled', ...disabledBadge });
    } else if (primaryBadge) {
      badges.push({ key: 'primary', ...primaryBadge });
    } else if (!disabledBadge && hiddenBadge) {
      badges.push({ key: 'hidden', ...hiddenBadge });
    }
  } else {
    /**
     * Show a sale status badge + disabled or hidden badge
     */
    if (primaryBadge) badges.push({ key: 'primary', ...primaryBadge });
    if (disabledBadge) badges.push({ key: 'disabled', ...disabledBadge });
    if (!disabledBadge && hiddenBadge) badges.push({ key: 'hidden', ...hiddenBadge });
  }

  if (badges.length === 0) {
    return null;
  }

  return (
    <div className="flex gap-x-1 flex-wrap">
      {badges.map(badge => (
        <div key={badge.key}>
          {badge.tooltip ? <Tooltip label={badge.tooltip}>{badge.content}</Tooltip> : badge.content}
        </div>
      ))}
    </div>
  );
}
